import { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import {
  ATTACHMENT_CAPS,
  deleteAttachment,
  getDownloadUrl,
  listAttachments,
  uploadAttachment,
  type AttachmentEntityType,
} from "@/api/attachments";
import type { AttachmentRead } from "@/api/types";

export interface UseAttachmentsParams {
  slug: string | null;
  entityType: AttachmentEntityType;
  /** When `null`, the hook is dormant — it does not fetch, mutations
   *  reject, and `attachments` is the empty array. This is the
   *  intended state during a buffer-mode upload (e.g. a not-yet-
   *  created task in TaskCreateDialog), where the Phase B
   *  integration uploads buffered files AFTER the parent entity is
   *  created and then flips this id to a real number. */
  entityId: number | null;
}

export interface UseAttachmentsResult {
  attachments: AttachmentRead[];
  isLoading: boolean;
  /** True after the first fetch resolves (success or failure). The
   *  consumer uses this to distinguish "still loading" from "loaded,
   *  empty list" — both have `attachments.length === 0`. */
  loaded: boolean;
  error: string | null;
  /** Hard cap from `ATTACHMENT_CAPS`. Exposed so the component can
   *  show "X / cap" in its header without re-importing the constant. */
  cap: number;
  reload: () => Promise<void>;
  /** Optimistic upload — the server response is appended on success;
   *  on failure the error bubbles for the caller's toast. */
  upload: (file: File) => Promise<AttachmentRead>;
  /** Optimistic remove with rollback on non-404 errors. A 404 is
   *  silently absorbed: someone else already deleted the row, the
   *  user just wins the race. */
  remove: (attachmentId: string) => Promise<void>;
  /** Resolve + open the short-lived signed URL. Caller decides what
   *  to do on failure (we just throw). */
  download: (attachmentId: string) => Promise<void>;
}

/**
 * Live-mode attachments hook. Plain useState/useEffect pattern
 * mirroring `useViews` — no React Query in this codebase.
 *
 * Buffer mode (a not-yet-created entity holds files in component
 * state, then flushes them after creation) is intentionally NOT
 * implemented here. The Phase B integration in TaskCreateDialog
 * will manage that buffer in its own component state, then mount
 * this hook against the freshly-created task id to flush the
 * queue. The hook's only buffer-mode-aware behavior is that it
 * accepts `entityId: null` cleanly.
 */
export function useAttachments({
  slug,
  entityType,
  entityId,
}: UseAttachmentsParams): UseAttachmentsResult {
  const [attachments, setAttachments] = useState<AttachmentRead[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Stale-fetch guard: when the (slug, entityType, entityId) tuple
  // changes mid-flight, drop responses for the old key.
  const latestKeyRef = useRef<string>("");

  const reload = useCallback(async () => {
    if (!slug || entityId == null) {
      setAttachments([]);
      setLoaded(true);
      setError(null);
      return;
    }
    const key = `${slug}::${entityType}::${entityId}`;
    latestKeyRef.current = key;
    setIsLoading(true);
    setError(null);
    try {
      const res = await listAttachments(slug, entityType, entityId);
      if (latestKeyRef.current !== key) return;
      setAttachments(res);
    } catch (err) {
      if (latestKeyRef.current !== key) return;
      setAttachments([]);
      setError(extractMessage(err));
    } finally {
      if (latestKeyRef.current === key) {
        setIsLoading(false);
        setLoaded(true);
      }
    }
  }, [slug, entityType, entityId]);

  useEffect(() => {
    setLoaded(false);
    void reload();
  }, [reload]);

  const upload = useCallback(
    async (file: File) => {
      if (!slug || entityId == null) {
        throw new Error("Cannot upload, entity is not ready.");
      }
      const created = await uploadAttachment(
        slug,
        entityType,
        entityId,
        file,
      );
      // Append rather than refetch — the list endpoint is one extra
      // round-trip we don't need; the server's response is already
      // the canonical row.
      setAttachments((prev) => [...prev, created]);
      return created;
    },
    [slug, entityType, entityId],
  );

  const remove = useCallback(
    async (attachmentId: string) => {
      if (!slug || entityId == null) {
        throw new Error("Cannot remove, entity is not ready.");
      }
      // Capture the pre-update snapshot via the setter callback so
      // we have something to roll back to on failure. setAttachments
      // never re-renders synchronously, but the callback IS run
      // synchronously, so `snapshot` is reliably populated before
      // we call the server.
      let snapshot: AttachmentRead[] = [];
      setAttachments((prev) => {
        snapshot = prev;
        return prev.filter((a) => a.id !== attachmentId);
      });
      try {
        await deleteAttachment(slug, entityType, entityId, attachmentId);
      } catch (err) {
        const status = axios.isAxiosError(err)
          ? err.response?.status
          : undefined;
        if (status === 404) {
          // Concurrent delete by another tab/operator. The row is
          // gone — our optimistic update is already correct.
          return;
        }
        setAttachments(snapshot);
        throw err;
      }
    },
    [slug, entityType, entityId],
  );

  const download = useCallback(
    async (attachmentId: string) => {
      if (!slug || entityId == null) {
        throw new Error("Cannot download, entity is not ready.");
      }
      const res = await getDownloadUrl(
        slug,
        entityType,
        entityId,
        attachmentId,
      );
      // `noopener,noreferrer` so the storage host can't access the
      // app's `window.opener`. The signed URL is short-lived, but
      // belt-and-braces.
      window.open(res.url, "_blank", "noopener,noreferrer");
    },
    [slug, entityType, entityId],
  );

  return {
    attachments,
    isLoading,
    loaded,
    error,
    cap: ATTACHMENT_CAPS[entityType],
    reload,
    upload,
    remove,
    download,
  };
}

function extractMessage(err: unknown): string {
  if (axios.isAxiosError(err)) {
    const detail = (err.response?.data as { detail?: unknown } | undefined)
      ?.detail;
    if (typeof detail === "string") return detail;
    if (err.code === "ERR_NETWORK") return "Cannot reach the server.";
  }
  return "Couldn't load attachments.";
}
